import { Map, List, Record, OrderedMap } from 'immutable';
import { storage } from 'lib/storage';
import {
  CompanyFeaturesStateRecord,
  CompanyFeaturesApiResponse,
  CompanyGeneralFeaturesRaw,
  DynamicFeaturesMap,
  ErpsFeatureRaw,
  ErpsFeatureRecord,
  GeneralFeatureRaw,
  GeneralFeaturesOrderedMap,
} from 'domain/companies/types';

export const SEARCH_TYPES = Object.freeze({
  byCompany: 'byCompany',
  byDocumentText: 'byDocumentText',
});

const SignParamsTokenFactory = Record({
  password: '',
  name: '',
  permissions: null,
});

const permissionsFactory = Record({
  users: new List(),
  roles: new List(),
});

export function permissionsAdapter({ users, roles }) {
  if (typeof users === 'undefined' && typeof roles === 'undefined') return null;
  return permissionsFactory({ users: List(users), roles: List(roles) });
}

function signParamsAdapter(sp, token) {
  const p = sp[token] && sp[token].permissions;
  return SignParamsTokenFactory({
    ...sp[token],
    permissions: p ? permissionsAdapter(p) : null,
  });
}

export function signParamsFactory(signParams) {
  const spf = new Record({
    guid: '',
    token1: SignParamsTokenFactory(),
    token2: SignParamsTokenFactory(),
  });
  if (typeof signParams !== 'undefined') {
    const token1 = signParamsAdapter(signParams, 'token1');
    const token2 = signParamsAdapter(signParams, 'token2');
    return spf({ guid: signParams.guid, token1, token2 });
  }
  return spf();
}

export const ERP_NAMES = Object.freeze({
  acumatica: 'ACUMATICA',
  acumaticaAdvanced: 'ACUMATICA_ADVANCED',
  mdNavision: 'MICROSOFT_DYNAMICS_NAV',
  mdBC: 'MICROSOFT_DYNAMICS_BC',
  qbo: 'QBO',
  qbd: 'QBD',
  xero: 'XERO',
  zoho: 'ZOHO',
  sageSA: 'SAGE_SA',
  sage: 'SAGE',
  priorityPro: 'PRIORITY_PRO',
  priorityProSQL: 'PRIORITY_PRO_SQL',
  hash: 'HASH',
  hashWeb: 'HASH_WEB',
  fox: 'FOX',
  sap: 'SAP',
  sap4hana: 'SAP4HANA',
  defaultErp: 'DEFAULT_ERP',
  netsuite: 'NETSUITE',
});

export const ERP_TITLES = {
  [ERP_NAMES.acumatica]: 'Acumatica',
  [ERP_NAMES.acumaticaAdvanced]: 'Acumatica Advanced',
  [ERP_NAMES.mdNavision]: 'Microsoft Dynamics Navision',
  [ERP_NAMES.mdBC]: 'Microsoft Dynamics 365 Business Central',
  [ERP_NAMES.qbo]: 'QuickBooks Online',
  [ERP_NAMES.qbd]: 'QuickBooks Desktop',
  [ERP_NAMES.xero]: 'XERO',
  [ERP_NAMES.zoho]: 'Zoho Books',
  [ERP_NAMES.sageSA]: 'Sage Business Cloud (South Africa)',
  [ERP_NAMES.sage]: 'Sage Business Cloud (Global)',
  [ERP_NAMES.priorityPro]: 'Priority',
  [ERP_NAMES.priorityProSQL]: 'Priority SQL',
  [ERP_NAMES.hash]: 'H-ERP',
  [ERP_NAMES.hashWeb]: 'H-WEB',
  [ERP_NAMES.fox]: 'Custom ERP',
  [ERP_NAMES.sap]: 'SAP Business One',
  [ERP_NAMES.sap4hana]: 'SAP S/4HANA',
  [ERP_NAMES.netsuite]: 'Netsuite',
  [ERP_NAMES.defaultErp]: 'Default',
};

const ErpsFactory = Record<ErpsFeatureRaw>({
  [ERP_NAMES.acumatica]: false,
  [ERP_NAMES.acumaticaAdvanced]: false,
  [ERP_NAMES.mdNavision]: false,
  [ERP_NAMES.mdBC]: false,
  [ERP_NAMES.qbo]: false,
  [ERP_NAMES.qbd]: false,
  [ERP_NAMES.xero]: false,
  [ERP_NAMES.zoho]: false,
  [ERP_NAMES.sageSA]: false,
  [ERP_NAMES.sage]: false,
  [ERP_NAMES.priorityPro]: false,
  [ERP_NAMES.priorityProSQL]: false,
  [ERP_NAMES.hash]: false,
  [ERP_NAMES.hashWeb]: false,
  [ERP_NAMES.fox]: false,
  [ERP_NAMES.sap]: false,
  [ERP_NAMES.sap4hana]: false,
  [ERP_NAMES.netsuite]: false,
  [ERP_NAMES.defaultErp]: false,
});

export const FeatureSetFactory = Record<GeneralFeatureRaw>({
  value: false,
  display_name: '',
  state: 'hidden',
  help: null,
  show_hint: false,
  category: null,
});

export const applicationsFactory = new Record({
  slack: {
    status: 'not_connected',
    workspace: '',
  },
});

const companyGeneralFeaturesAdapter = (data: CompanyGeneralFeaturesRaw = {}): GeneralFeaturesOrderedMap => {
  return OrderedMap(data).mapEntries(([K, V]) => [K, FeatureSetFactory(V)]) as GeneralFeaturesOrderedMap;
};

export function CompanyFactory(company) {
  const cf = new Record({
    checksum: '',
    cname: '',
    id: '',
    organizationId: '',
    corresp: false,
    description: '',
    email: '',
    'email.confidential': '',
    language: '',
    logo: '',
    pinned: false,
    unprocessed: 0,
    notifications: 0,
    pendingApproval: 0,
    signParams: signParamsFactory(),
    generalParams: OrderedMap(),
    dateFormat: 'DD/MM/YYYY',
    isConnectedToErp: false,
    whatsappPhone: null,
  });
  if (typeof company !== 'undefined') {
    const { signParams, generalParams, logo, is_connected_to_erp, pending_approval, whatsapp_phone, ...rest } = company;
    const signParamsR = signParamsFactory(signParams);
    const generalParamsR = companyGeneralFeaturesAdapter(generalParams);
    return cf({
      ...rest,
      signParams: signParamsR,
      generalParams: generalParamsR,
      logo,
      isConnectedToErp: is_connected_to_erp,
      pendingApproval: pending_approval,
      whatsappPhone: whatsapp_phone,
    });
  }
  return cf();
}

export const sortProp = (key) => (map) => map.get(key);
export const sortByNumbersAndName = (bNot, aNot, bName, aName) => {
  if (bNot > aNot) return 1;
  if (bNot < aNot) return -1;
  if (bNot === aNot) return aName.localeCompare(bName);
  return 0;
};

export function companiesAdapter(companies) {
  return companies.reduce((a, v) => a.set(v.id, CompanyFactory(v)), new Map());
}

export function companiesFilter(companies, term, isSupport) {
  return companies.filter((f) => {
    if (Array.isArray(term)) {
      return term.includes(f.get('id', ''));
    }
    return (
      ~f.get('cname', '').toLocaleLowerCase().indexOf(term.toLocaleLowerCase()) ||
      (isSupport && ~f.get('id', '').indexOf(term))
    );
  });
}

export function companiesFilterForGrid(companies, term, isSupport) {
  return companies.filter((f) => {
    if (Array.isArray(term)) {
      return term.includes(f.id);
    }

    const name = f.name?.toLocaleLowerCase() || '';
    const id = f.id || '';

    const search = term.toLocaleLowerCase();

    const nameMatches = name.includes(search);
    const idMatches = isSupport && id.includes(term);

    return nameMatches || idMatches;
  });
}

export function companiesSort(companies, sort, notifications) {
  const list = companies
    .map((item) => item.set('notifications', notifications.get(item.id, 0)))
    .toList()
    .sortBy(sortProp('cname'));
  const filters = {
    favorites: (a, b) => b.pinned - a.pinned,
    unread: (a, b) => sortByNumbersAndName(b.notifications, a.notifications, b.cname, a.cname),
    new: (a, b) => sortByNumbersAndName(b.unprocessed, a.unprocessed, b.cname, a.cname),
    pendingApproval: (a, b) => sortByNumbersAndName(b.pendingApproval, a.pendingApproval, b.cname, a.cname),
  };
  return filters[sort] ? list.sort(filters[sort]) : list;
}

export const signerFactory = new Record({
  password: '',
  token_name: null,
  name: '',
});

const contactFactory = new Record({
  phone: '',
  first_name: '',
  last_name: '',
  email: '',
});

export const signingsFactory = (data = {}) => {
  const signings = new Record({
    guid: '',
    tokens: new List(),
  });
  const record = signings(data);
  if (data.tokens) {
    return record.set('tokens', List(data.tokens.map((item) => signerFactory(item))));
  }
  return record;
};

export function configurationCompanyFactory(data) {
  const company = new Record({
    picture: '',
    name: '',
    city: '',
    country: '',
    currency: '',
    email_box: '',
    confidential_email_box: '',
    contact: contactFactory(),
    signings: signingsFactory(),
    signingsCompanyId: null,
    street: '',
    vat_number: '',
    id: null,
    registration_number: '',
    whatsapp_phone: null,
    applications: applicationsFactory(),
    amounts_representation: '',
    preparation_days: null,
  });
  if (typeof data !== 'undefined') {
    const { contact, signings, ...rest } = data;
    return company({
      ...rest,
      contact: contactFactory(contact),
      signings: signingsFactory(signings),
    });
  }
  return company();
}

export const companyFeaturesAdapter = (
  data: CompanyFeaturesApiResponse,
): Map<string, GeneralFeaturesOrderedMap | DynamicFeaturesMap | ErpsFeatureRecord> => {
  const { erp: erpsRaw, dynamic_features: dynamicFeaturesRaw, ...rest } = data;

  // Explicitly type the remaining properties as CompanyGeneralFeaturesRaw
  const generalFeaturesRaw = rest as CompanyGeneralFeaturesRaw;

  // Create feature items from the rest of the data
  const generalFeatures = companyGeneralFeaturesAdapter(generalFeaturesRaw);

  const erps = ErpsFactory(erpsRaw);
  const dynamicFeatures = Map(dynamicFeaturesRaw);

  return Map({ generalFeatures, dynamicFeatures, erps });
};

export const CompanyFeaturesFactory = Record<CompanyFeaturesStateRecord>({
  isLoaded: false,
  companyID: null,
  generalFeatures: OrderedMap(),
  dynamicFeatures: Map(),
  erps: ErpsFactory(),
});

export function configurationCompaniesAdapter(data) {
  return new List(data.map(configurationCompanyFactory));
}

export const CompaniesFactory = Record({
  list: Map(),
  filter: '',
  searchText: '',
  searchType: SEARCH_TYPES.byCompany,
  configurationCompany: configurationCompanyFactory(),
  companyFeatures: CompanyFeaturesFactory(),
  sort: 'favorites',
  refreshCompany: '',
  isListLoaded: false,
  isConfigurationLoaded: false,
  companiesWorkSpace: Map(),
});

export function companiesStoreAdapter(props) {
  const workSpaceStr = storage().companiesWorkSpaceType().get();
  const companiesWorkSpace = workSpaceStr ? JSON.parse(workSpaceStr) : {};
  return CompaniesFactory({
    ...props,
    companiesWorkSpace: Map(companiesWorkSpace),
  });
}

export function createCompanyAdapter(data) {
  const { id, name, picture, email_box } = data;

  return Map({
    [id]: CompanyFactory({
      cname: name,
      id,
      logo: picture,
      email: email_box,
    }),
  });
}

export function checkPermissions(p, id, role) {
  if (!p) return true;
  return p.users.some((u) => u === id) || p.roles.some((r) => r === role);
}
